import { SystemDiagramNode } from "src/api/generated/postgres/db/types/public_/tables/systemDiagramNode";
import { FormCommonProps } from "src/components/common/forms/types";
import { getSystemDiagramNodeShapeLabels } from "src/api/generated/postgres/db/types/public_/enums/systemDiagramNodeShape";
import i18n from "i18next";
import {
	SystemDiagramNodeCrudApi,
	SystemDiagramNodeCrudApi_FormInitialData,
} from "src/api/generated/systemDiagrams/management/systemDiagramNodeCrudApi";
import { AavoForm } from "src/components/common/forms/AavoForm.tsx";
import { FormTextField } from "src/components/common/forms/fields/FormTextField";
import { FormSelectField } from "src/components/common/forms/fields/FormSelectField";
import { enumOptions } from "src/components/common/forms/fields/selectionFieldUtils";
import { SystemDiagramFunctionParamsForm } from "src/components/views/erp/common/systemDiagrams/functionParamForms/SystemDiagramFunctionParamsForm";
import { AsyncFetchRender } from "src/components/common/async/AsyncFetchRender.tsx";
import { DeepPartial, useForm } from "react-hook-form";
import { requireRule } from "src/components/common/forms/validation";

export interface SystemDiagramNodeFormProps extends FormCommonProps<number> {
	systemDiagramId: number;
	systemDiagramNodeId: number | undefined;
}

export const SystemDiagramNodeForm = ({
	systemDiagramId,
	systemDiagramNodeId,
	...other
}: SystemDiagramNodeFormProps) => {
	return (
		<AsyncFetchRender
			fetch={() =>
				SystemDiagramNodeCrudApi.getFormInitialData({
					systemDiagramId,
					systemDiagramNodeId,
				})
			}
			content={(formInitialData) => (
				<SystemDiagramNodeFormContent
					systemDiagramId={systemDiagramId}
					defaultValues={
						formInitialData.systemDiagramNode ?? createDefaultValues(systemDiagramId)
					}
					formInitialData={formInitialData}
					{...other}
				/>
			)}
		/>
	);
};

interface SystemDiagramNodeFormContentProps extends FormCommonProps<number> {
	systemDiagramId: number;
	defaultValues: DeepPartial<SystemDiagramNode>;
	formInitialData: SystemDiagramNodeCrudApi_FormInitialData;
}

const SystemDiagramNodeFormContent = ({
	formInitialData,
	defaultValues,
	onFormEdited,
	...other
}: SystemDiagramNodeFormContentProps) => {
	const { functionOptions } = formInitialData;

	const {
		control,
		handleSubmit,
		watch,
		formState: { isDirty },
	} = useForm<SystemDiagramNode>({
		mode: "all",
		defaultValues: defaultValues,
	});

	if (onFormEdited) watch(onFormEdited);

	return (
		<AavoForm handleSubmit={handleSubmit} isDirty={isDirty} submit={submitForm} {...other}>
			<FormTextField
				control={control}
				name={"name"}
				label={i18n.t("name")}
				rules={requireRule()}
				autoFocus
			/>
			<FormSelectField
				control={control}
				name={"shape"}
				label={i18n.t("shape")}
				options={enumOptions(getSystemDiagramNodeShapeLabels())}
				rules={requireRule()}
				disableClearable
			/>
			<FormSelectField
				control={control}
				name={"functionId"}
				label={i18n.t("function")}
				options={functionOptions.map((f) => ({ key: f.id, label: f.label }))}
				rules={requireRule()}
				disableClearable
			/>
			<SystemDiagramFunctionParamsForm
				control={control}
				nodeFunction={functionOptions.find((f) => f.id === watch("functionId"))}
				formInitialData={formInitialData}
			/>
		</AavoForm>
	);
};

const createDefaultValues = (systemDiagramId: number): DeepPartial<SystemDiagramNode> => ({
	name: "",
	shape: "RECTANGULAR",
	systemDiagramId: systemDiagramId,
});

const submitForm = async (systemDiagramNode: SystemDiagramNode): Promise<number> => {
	const result = await SystemDiagramNodeCrudApi.save({
		systemDiagramNode,
	});
	return result.systemDiagramNodeId;
};
